<template>
  <div v-if="editor" class="tiptap-container">
    <div v-if="isEditable && hasToolbar" class="control-group">
      <TipTapToolbarButton
        icon="mdi-undo"
        title="Undo"
        @click="editor.chain().focus().undo().run()"
      />
      <TipTapToolbarButton
        icon="mdi-redo"
        title="Redo"
        @click="editor.chain().focus().redo().run()"
      />
      <TipTapToolbarButton
        icon="mdi-format-bold"
        title="Bold"
        :is-active="isExtEnableAndActive(editor, 'bold')"
        @click="editor.chain().focus().toggleBold().run()"
      />
      <TipTapToolbarButton
        icon="mdi-format-italic"
        title="Italic"
        :is-active="isExtEnableAndActive(editor, 'italic')"
        @click="editor.chain().focus().toggleItalic().run()"
      />
      <TipTapToolbarButton
        icon="mdi-format-underline"
        title="Underline"
        :is-active="isExtEnableAndActive(editor, 'underline')"
        @click="editor.chain().focus().toggleUnderline().run()"
      />
      <TipTapToolbarButton
        icon="mdi-format-strikethrough-variant"
        title="Strikethrough"
        :is-active="isExtEnableAndActive(editor, 'strike')"
        @click="editor.chain().focus().toggleStrike().run()"
      />
      <TipTapToolbarButton
        icon="mdi-code-tags"
        title="Code"
        :is-active="isExtEnableAndActive(editor, 'code')"
        @click="editor.chain().focus().toggleCode().run()"
      />
      <TipTapToolbarButton
        icon="mdi-format-quote-close"
        title="Blockquote"
        :is-active="isExtEnableAndActive(editor, 'blockquote')"
        @click="editor.chain().focus().toggleBlockquote().run()"
      />

      <TipTapColorControl
        v-if="editor?.isActive('table')"
        :editor="editor"
        icon="mdi-format-color-text"
        title="Font Colour"
        clear-title="Clear Font Colour"
        attribute-name="color"
        apply-command="setColor"
        clear-command="unsetColor"
      />
      <TipTapColorControl
        v-if="editor?.isActive('table')"
        :editor="editor"
        icon="mdi-format-color-highlight"
        title="Highlight"
        clear-title="Clear Highlight"
        attribute-name="backgroundColor"
        apply-command="setBackgroundColor"
        clear-command="unsetBackgroundColor"
      />
      <TipTapColorControl
        v-if="editor?.isActive('table')"
        :editor="editor"
        icon="mdi-format-color-fill"
        title="Cell Fill"
        clear-title="Clear Cell Fill"
        attribute-name="backgroundColor"
        :attribute-targets="cellFillTargets"
        apply-command="setCellBackgroundColor"
        clear-command="unsetCellBackgroundColor"
      />

      <TipTapToolbarButton
        icon="mdi-format-paragraph"
        title="Paragraph"
        :is-active="editor?.isActive('paragraph')"
        @click="editor.chain().focus().setParagraph().run()"
      />

      <TipTapToolbarButton
        icon="mdi-format-header-1"
        title="Heading 1"
        :is-active="editor?.isActive('heading', { level: 1 })"
        @click="editor.chain().focus().setHeading({ level: 1 }).run()"
      />
      <TipTapToolbarButton
        icon="mdi-format-header-2"
        title="Heading 2"
        :is-active="editor?.isActive('heading', { level: 2 })"
        @click="editor.chain().focus().setHeading({ level: 2 }).run()"
      />

      <TipTapToolbarButton
        icon="mdi-format-header-3"
        title="Heading 3"
        :is-active="editor?.isActive('heading', { level: 3 })"
        @click="editor.chain().focus().setHeading({ level: 3 }).run()"
      />

      <TipTapToolbarButton
        icon="mdi-format-header-4"
        title="Heading 4"
        :is-active="editor?.isActive('heading', { level: 4 })"
        @click="editor.chain().focus().setHeading({ level: 4 }).run()"
      />

      <TipTapToolbarButton
        icon="mdi-format-header-5"
        title="Heading 5"
        :is-active="editor?.isActive('heading', { level: 5 })"
        @click="editor.chain().focus().setHeading({ level: 5 }).run()"
      />

      <TipTapToolbarButton
        icon="mdi-format-list-bulleted"
        title="Bullet list"
        :is-active="isExtEnableAndActive(editor, 'bulletList')"
        @click="editor.chain().focus().toggleBulletList().run()"
      />
      <TipTapToolbarButton
        icon="mdi-format-list-numbered"
        title="Numbered list"
        :is-active="isExtEnableAndActive(editor, 'orderedList')"
        @click="editor.chain().focus().toggleOrderedList().run()"
      />

      <TipTapToolbarButton
        icon="mdi-link"
        :title="editor.isActive('link') ? 'Update Link' : 'Add Link'"
        :is-active="editor.isActive('link')"
        @click="$emit('showLinkEditorDialog')"
      />
      <TipTapToolbarButton
        v-if="editor.isActive('link')"
        icon="mdi-link-off"
        title="Remove Link"
        @click="$emit('removeLinkMarkFromSelection')"
      />
      <TipTapToolbarButton
        icon="mdi-table-large"
        title="Create Table"
        :is-active="editor.isActive('table')"
        @click="
          editor
            .chain()
            .focus()
            .insertTable({ rowsCount: 3, colsCount: 3, withHeaderRow: true })
            .run()
        "
      />
      <TipTapToolbarButton
        v-if="editor?.isActive('table')"
        icon="mdi-table-large-plus"
        title="Add Table"
        @click="
          editor
            .chain()
            .focus()
            .insertTable({ rowsCount: 3, colsCount: 3, withHeaderRow: true })
            .run()
        "
      />
      <TipTapToolbarButton
        v-if="editor?.isActive('table')"
        icon="mdi-table-large-remove"
        title="Delete Table"
        @click="editor.chain().focus().deleteTable().run()"
      />
      <TipTapToolbarButton
        v-if="editor?.isActive('table')"
        icon="mdi-table-row-plus-before"
        title="Add Row Before"
        @click="editor.chain().focus().addRowBefore().run()"
      />
      <TipTapToolbarButton
        v-if="editor?.isActive('table')"
        icon="mdi-table-row-plus-after"
        title="Add Row After"
        @click="editor.chain().focus().addRowAfter().run()"
      />
      <TipTapToolbarButton
        v-if="editor?.isActive('table')"
        icon="mdi-table-row-remove"
        title="Delete Row"
        @click="editor.chain().focus().deleteRow().run()"
      />
      <TipTapToolbarButton
        v-if="editor?.isActive('table')"
        icon="mdi-table-column-plus-before"
        title="Add Column Before"
        @click="editor.chain().focus().addColumnBefore().run()"
      />
      <TipTapToolbarButton
        v-if="editor?.isActive('table')"
        icon="mdi-table-column-plus-after"
        title="Add Column After"
        @click="editor.chain().focus().addColumnAfter().run()"
      />
      <TipTapToolbarButton
        v-if="editor?.isActive('table')"
        icon="mdi-table-column-remove"
        title="Delete Column"
        @click="editor.chain().focus().deleteColumn().run()"
      />
      <TipTapToolbarButton
        v-if="editor?.isActive('table')"
        icon="mdi-table-row"
        title="Toggle Header Row"
        @click="editor.chain().focus().toggleHeaderRow().run()"
      />
      <TipTapToolbarButton
        v-if="editor?.isActive('table')"
        icon="mdi-table-column"
        title="Toggle Header Column"
        @click="editor.chain().focus().toggleHeaderColumn().run()"
      />
      <TipTapToolbarButton
        v-if="editor?.isActive('table')"
        icon="mdi-table-merge-cells"
        title="Merge Cells"
        @click="editor.chain().focus().mergeCells().run()"
      />
      <TipTapToolbarButton
        v-if="editor?.isActive('table')"
        icon="mdi-table-split-cell"
        title="Split Cell"
        @click="editor.chain().focus().splitCell().run()"
      />

      <TipTapToolbarButton
        v-if="supportImage"
        icon="mdi-image"
        title="Add Image"
        @click="$emit('addImage')"
      />

      <template v-if="editor.isActive('image')">
        <TipTapToolbarButton
          v-for="button in layoutButtons"
          :key="button.layout"
          :icon="button.icon"
          :title="button.title"
          :is-active="isImageLayoutActive(button.layout)"
          @click="editor.chain().focus().setImageLayout(button.layout).run()"
        />
      </template>

      <TipTapToolbarButton
        v-if="supportContextKey"
        icon="mdi-clipboard-account"
        title="Context Key"
        @click="$emit('showContextKeys')"
      />
      <TipTapToolbarButton
        v-if="usePlaceholder !== null"
        icon="mdi-clipboard-text-outline"
        title="Add Placeholder"
        @click="$emit('insertPlaceholderText')"
      />
      <TipTapToolbarButton
        v-if="allowPrintPreview"
        icon="mdi-file-document-outline"
        :title="printPreview ? 'Hide printable area' : 'Show printable area'"
        :is-active="printPreview"
        @click="printPreview = !printPreview"
      />
    </div>

    <BubbleMenu v-if="editor && hasBubbleMenu" :editor="editor" :image-layouts="imageLayouts" />

    <FloatingMenu v-if="editor && hasFloatingMenu" :editor="editor" />

    <div
      class="editor-content-container"
      :class="{ 'is-print-preview print-preview-content': printPreview }"
    >
      <editor-content
        :editor="editor"
        class="editor__content"
        :class="{ 'readable-content': printPreview }"
        :style="{
          height: height,
          '--editor-content-height': height,
          ...printableAreaStyle
        }"
      />
    </div>
  </div>
</template>

<script>
import { EditorContent } from '@tiptap/vue-3';
import TipTapToolbarButton from '@/components/content/tiptap/TipTapToolbarButton';
import TipTapColorControl from '@/components/content/tiptap/TipTapColorControl';
import BubbleMenu from '@/components/content/tiptap/BubbleMenu';
import FloatingMenu from '@/components/content/tiptap/FloatingMenu';
import isExtEnableAndActive from '@/components/content/tiptap/isExtEnableAndActive';
import initTiptapEditor from '@/components/content/tiptap/initTiptapEditor';
import { IMAGE_LAYOUTS, IMAGE_LAYOUT_BUTTONS } from '@/components/content/tiptap/imageWithLayout';
import { collectTableWidths } from '@/lib/collectTableWidths';
import { PDF_PAGE_GEOMETRY, PDF_EXPORT_FONT_HREF } from '@/constants';
import { PDF_LAYOUT_CSS } from '@/lib/pdfLayoutStyles';
import { scopeCss } from '@/lib/scopeCss';

const PDF_LAYOUT_STYLE_ID = 'pdf-layout-stylesheet';
const PDF_LAYOUT_FONT_ID = 'pdf-layout-webfont';
const PRINT_PREVIEW_SCOPE = '.print-preview-content';

// The same class written twice, deliberately, as the scope the export stylesheet is prefixed with.
// inside the preview the export's stylesheet must be the authority - that is the whole
// point of the toggle. Vue compiles this component's scoped styles with an extra `[data-v-hash]`
// attribute selector, so a rule like `table td` here becomes `[data-v-hash] .tiptap table td` =
// specificity (0,2,2), which outranks the scoped export rule `.print-preview-content
// .readable-content td` = (0,2,1). The editor's own approximation therefore won, and the preview
// quietly showed the editor's table borders and padding instead of the export's.
//
// Counted across the two sheets: at a single-class scope the editor outranks the export sheet in
// 33 places (td/th border and padding, th font-weight, list margins, code, img); doubling the class
// to (0,3,1) leaves 2, and both should stay - the `table[style]` width guard that contains
// overflowing tables, and `table img` sizing.
//
// Repeating the class is the standard way to add specificity without changing what is matched.
const PRINT_PREVIEW_STYLE_SCOPE = `${PRINT_PREVIEW_SCOPE}${PRINT_PREVIEW_SCOPE}`;

export default {
  name: 'TipTapEditor',

  components: {
    EditorContent,
    TipTapToolbarButton,
    TipTapColorControl,
    BubbleMenu,
    FloatingMenu
  },

  props: {
    modelValue: {
      type: String,
      default: ''
    },
    hasToolbar: {
      type: Boolean,
      default: true
    },
    allowPrintPreview: {
      type: Boolean,
      default: false
    },
    hasBubbleMenu: {
      type: Boolean,
      default: true
    },
    hasFloatingMenu: {
      type: Boolean,
      default: true
    },
    isEditable: {
      type: Boolean,
      default: true
    },
    autoFocus: {
      type: [Boolean, Number, String, null],
      default: true
    },
    editorOptions: {
      type: Object,
      default: () => ({})
    },
    supportContextKey: {
      type: Boolean,
      default: false
    },
    supportImage: {
      type: Boolean,
      default: false
    },
    imageLayouts: {
      type: Array,
      default: () => IMAGE_LAYOUTS
    },
    usePlaceholder: {
      type: Boolean,
      default: false
    },
    height: {
      type: String,
      default: '20rem'
    }
  },

  emits: [
    'update:modelValue',
    'addImage',
    'showContextKeys',
    'insertPlaceholderText',
    'showLinkEditorDialog',
    'removeLinkMarkFromSelection',
    'handlePasteContent'
  ],

  setup() {
    return {
      isExtEnableAndActive
    };
  },
  data() {
    return {
      /** @type {Editor} */
      editor: null,
      printPreview: false,
      cellFillTargets: ['tableCell', 'tableHeader']
    };
  },

  computed: {
    printableAreaStyle() {
      if (!this.printPreview) {
        return {};
      }

      return {
        '--pdf-content-width': `${PDF_PAGE_GEOMETRY.contentWidth}px`,
        '--pdf-content-height': `${PDF_PAGE_GEOMETRY.contentHeight}px`,
        '--pdf-preview-page-padding': `${PDF_PAGE_GEOMETRY.previewPagePadding}px`
      };
    },

    layoutButtons() {
      return IMAGE_LAYOUT_BUTTONS.filter((button) => this.imageLayouts.includes(button.layout));
    }
  },

  watch: {
    printPreview(enabled) {
      if (enabled) {
        this.ensureReadableContentStylesheet();
        this.ensureReadableContentWebfont();
      }
    },

    modelValue(value) {
      const isDifferent = this.editor.getHTML() !== value; // HTML
      if (isDifferent) {
        // Save cursor position
        const { from, to } = this.editor.state.selection;
        this.editor.commands.setContent(value, {
          parseOptions: {
            preserveWhitespace: 'full'
          }
        });
        this.editor.commands.setTextSelection({ from, to });
      }
    },
    isEditable(value) {
      this.editor.setEditable(value);
    },
    autoFocus(value) {
      this.editor.setOptions({ autofocus: value });
    }
  },

  mounted() {
    this.initEditor();
  },

  beforeUnmount() {
    this.editor.destroy();
  },

  methods: {
    getEditor() {
      return this.editor;
    },

    /**
     * Image layout controls are duplicated here from the bubble menu because an
     * image inside a table gets no bubble menu at all (the table selection wins
     * in BubbleMenu.nodeType), leaving the toolbar as the only place to align it.
     */
    isImageLayoutActive(layout) {
      return this.editor.getAttributes('image').layout === layout;
    },

    initEditor() {
      this.editor = initTiptapEditor({
        // register extensions
        content: this.modelValue, // set the initial content
        autofocus: this.autoFocus,
        editable: this.isEditable,
        onUpdate: ({ editor }) => {
          const parser = new DOMParser();
          const parsedHtml = parser.parseFromString(editor.getHTML(), 'text/html');
          const tableElements = parsedHtml.getElementsByTagName('table');

          // Fix for table column resizing - Setting data attributes for table width.
          if (tableElements.length) {
            const domTableWidths = this.getElementWidths(this.editor.view.dom);

            Array.prototype.forEach.call(tableElements, (table, index) => {
              if (domTableWidths[index]) {
                const { tableWidth, wrapperWidth } = domTableWidths[index];

                const dataTableWidth = parsedHtml.createAttribute('data-tablewidth');
                // A 0 table width means TipTap has not given this table an explicit width yet
                // (not every column has been resized). Persist it blank so
                // `updateTableColumnsWidth` takes its documented `tableWidth = wrapperWidth`
                // fallback rather than treating a literal "0" as a real measurement.
                dataTableWidth.value = tableWidth > 0 ? String(tableWidth) : '';

                const dataTableWrapperWidth = parsedHtml.createAttribute('data-wrapperwidth');
                dataTableWrapperWidth.value = wrapperWidth > 0 ? String(wrapperWidth) : '';

                table.attributes.setNamedItem(dataTableWidth);
                table.attributes.setNamedItem(dataTableWrapperWidth);
              }
            });
            this.$emit('update:modelValue', parsedHtml.body.innerHTML);
          } else {
            this.$emit('update:modelValue', this.editor.getHTML());
          }
        },
        editorProps: {
          attributes: {
            class: 'tiptap-editor'
          },
          clipboardTextParser: (text) => {
            this.$emit('handlePasteContent', text);
          }
        },
        ...this.editorOptions
      });
    },

    getElementWidths(dom) {
      return collectTableWidths(dom);
    },

    ensureReadableContentStylesheet() {
      if (document.getElementById(PDF_LAYOUT_STYLE_ID)) {
        return;
      }

      const style = document.createElement('style');
      style.id = PDF_LAYOUT_STYLE_ID;
      style.textContent = scopeCss(PDF_LAYOUT_CSS, PRINT_PREVIEW_STYLE_SCOPE);
      document.head.appendChild(style);
    },

    ensureReadableContentWebfont() {
      if (document.getElementById(PDF_LAYOUT_FONT_ID)) {
        return;
      }

      const link = document.createElement('link');
      link.id = PDF_LAYOUT_FONT_ID;
      link.rel = 'stylesheet';
      link.href = PDF_EXPORT_FONT_HREF;
      document.head.appendChild(link);
    }
  }
};
</script>

<style scoped lang="scss">
/* Basic editor styles */
:deep(.tiptap) {
  --color-prettylights-syntax-comment: #6e7781;
  --color-prettylights-syntax-constant: #0550ae;
  --color-prettylights-syntax-entity: #8250df;
  --color-prettylights-syntax-storage-modifier-import: #24292f;
  --color-prettylights-syntax-entity-tag: #116329;
  --color-prettylights-syntax-keyword: #cf222e;
  --color-prettylights-syntax-string: #0a3069;
  --color-prettylights-syntax-variable: #953800;
  --color-prettylights-syntax-brackethighlighter-unmatched: #82071e;
  --color-prettylights-syntax-invalid-illegal-text: #f6f8fa;
  --color-prettylights-syntax-invalid-illegal-bg: #82071e;
  --color-prettylights-syntax-carriage-return-text: #f6f8fa;
  --color-prettylights-syntax-carriage-return-bg: #cf222e;
  --color-prettylights-syntax-string-regexp: #116329;
  --color-prettylights-syntax-markup-list: #3b2300;
  --color-prettylights-syntax-markup-heading: #0550ae;
  --color-prettylights-syntax-markup-italic: #24292f;
  --color-prettylights-syntax-markup-bold: #24292f;
  --color-prettylights-syntax-markup-deleted-text: #82071e;
  --color-prettylights-syntax-markup-deleted-bg: #ffebe9;
  --color-prettylights-syntax-markup-inserted-text: #116329;
  --color-prettylights-syntax-markup-inserted-bg: #dafbe1;
  --color-prettylights-syntax-markup-changed-text: #953800;
  --color-prettylights-syntax-markup-changed-bg: #ffd8b5;
  --color-prettylights-syntax-markup-ignored-text: #eaeef2;
  --color-prettylights-syntax-markup-ignored-bg: #0550ae;
  --color-prettylights-syntax-meta-diff-range: #8250df;
  --color-prettylights-syntax-brackethighlighter-angle: #57606a;
  --color-prettylights-syntax-sublimelinter-gutter-mark: #8c959f;
  --color-prettylights-syntax-constant-other-reference-link: #0a3069;
  --color-fg-default: #24292f;
  --color-fg-muted: #57606a;
  --color-fg-subtle: #6e7781;
  --color-canvas-default: #fff;
  --color-canvas-subtle: #f6f8fa;
  --color-border-default: #d0d7de;
  --color-border-muted: hsl(210deg 18% 87% / 100%);
  --color-neutral-muted: rgb(175 184 193 / 20%);
  --color-accent-fg: #0969da;
  --color-accent-emphasis: #0969da;
  --color-attention-subtle: #fff8c5;
  --color-danger-fg: #cf222e;
  /* Local CSS variables used by the editor styles */
  --purple-light: #f3e8ff;
  --black: #000000;
  --white: #ffffff;
  --gray-1: #f3f4f6;
  --gray-2: #e5e7eb;
  --gray-3: #d1d5db;
  min-height: calc(100% - 20px);

  :first-child {
    margin-top: 0;
  }

  p {
    margin-bottom: 16px !important;
  }

  /* List styles */
  ul,
  ol {
    padding-left: 1rem;

    li p,
    li ol,
    li ul {
      margin: 0;
    }
  }

  ol ol {
    list-style-type: lower-alpha;
  }

  ol ol ol {
    list-style-type: lower-roman;
  }

  /* Heading styles */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    line-height: 1.1;
    // margin-top: 2.5rem;
    text-wrap: pretty;
  }

  h1,
  h2,
  h3,
  h4,
  h5 {
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
  }

  h1 {
    font-size: 1.4rem;
  }

  h2 {
    font-size: 1.2rem;
  }

  h3 {
    font-size: 1.1rem;
  }

  h4 {
    font-size: 1rem;
  }

  h5 {
    font-size: 0.9rem;
  }

  /* Code and preformatted text styles */
  code {
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 0.4rem;
    color: rgba(0, 0, 0, 0.8);
    padding: 0.25em 0.3em;
    font-weight: bold;

    display: inline-block;
    font-size: 0.8rem;
  }

  pre {
    background: var(--black);
    border-radius: 0.5rem;
    color: var(--white);
    font-family: 'JetBrainsMono', monospace;
    margin: 1.5rem 0;
    padding: 0.75rem 1rem;

    code {
      background: none;
      color: inherit;
      font-size: 0.8rem;
      padding: 0;
    }
  }

  blockquote {
    border-left: 3px solid var(--gray-3);
    margin: 1.5rem 0;
    padding-left: 1rem;
    font-style: italic;
  }

  hr {
    border: none;
    border-top: 1px solid var(--gray-2);
    margin: 2rem 0;
  }

  img {
    display: block;
    max-width: 80%;
    height: auto;
    margin: 0 auto;
  }

  /*
   * Image layout modes (set via the image bubble menu / data-layout attribute).
   * Images are wrapped in a `.image-resize-view` NodeView span that carries the
   * data-layout, so layout is applied to the wrapper; the bare `img[data-layout]`
   * rules remain as a fallback for any non-NodeView render path.
   */
  /*
   * No data-layout at all = legacy image, authored before this feature. Keep
   * the pre-feature rendering (centered block, 80% cap) so simply opening a
   * page in the editor never re-flows images the author has not touched.
   */
  .image-resize-view:not([data-layout]) {
    display: block;
    max-width: 80%;
    margin: 0 auto;
  }

  img[data-layout='inline'],
  .image-resize-view[data-layout='inline'] {
    display: inline-block;
    margin: 0 0.25rem;
    vertical-align: middle;
  }

  img[data-layout='left-wrap'],
  .image-resize-view[data-layout='left-wrap'] {
    display: block;
    float: left;
    margin: 0.25rem 1rem 0.5rem 0;
  }

  img[data-layout='right-wrap'],
  .image-resize-view[data-layout='right-wrap'] {
    display: block;
    float: right;
    margin: 0.25rem 0 0.5rem 1rem;
  }

  /* No-wrap family: alone on the line, differing only in which side the auto
     margin falls on. */
  img[data-layout='left'],
  .image-resize-view[data-layout='left'] {
    display: block;
    float: none;
    margin: 0.5rem auto 0.5rem 0;
  }

  img[data-layout='center'],
  .image-resize-view[data-layout='center'] {
    display: block;
    float: none;
    margin: 0.5rem auto;
  }

  img[data-layout='right'],
  .image-resize-view[data-layout='right'] {
    display: block;
    float: none;
    margin: 0.5rem 0 0.5rem auto;
  }

  /*
   * The wrapper sizes to its image; width lives on the inner <img> (px),
   * so reset the base `img { max-width: 80% }` cap for wrapped images and
   * let the wrapper width follow the image.
   */
  .image-resize-view {
    position: relative;
    width: fit-content;
    max-width: 100%;
    line-height: 0;
  }

  .image-resize-view img {
    max-width: 100%;
    height: auto;
  }

  /* Highlight + move affordance when the image NodeView is selected. */
  .image-resize-view.is-resizable img {
    outline: 2px solid var(--color-accent-emphasis, #0969da);
    outline-offset: 1px;
    cursor: move;
  }

  .image-resize-handle {
    position: absolute;
    right: -4px;
    bottom: -4px;
    width: 12px;
    height: 12px;
    border: 2px solid var(--color-accent-emphasis, #0969da);
    border-radius: 50%;
    background: #ffffff;
    cursor: nwse-resize;
    touch-action: none;
  }

  /* center wrappers still need auto side-margins to center */
  .image-resize-view[data-layout='center'] {
    margin-left: auto;
    margin-right: auto;
  }

  table {
    display: table;
    width: 100% !important;
    max-width: 100% !important;
    overflow: auto;
    border-spacing: 0;
    border-collapse: collapse;

    margin-top: 0;
    margin-bottom: 16px;
  }
  table th {
    font-weight: var(--base-text-weight-semibold, 600);
  }

  /* CSPOL-1959: shared table style contract - keep border/padding/header values in sync with
     CEModuleRenderer.vue, CompareDialog.vue, UpdateCEModuleDialog.vue, src/constants.js
     (TABLE_STYLE_CONTRACT) and medusa/ce-app-api's server/templates/pdf-layout.html so a table
     renders with the same borders and column proportions in the editor, published page and PDF. */
  table th,
  table td {
    padding: 8px;
    border: 1px solid #dddddd;

    p {
      margin: 0;
    }
  }
  table tr {
    background-color: var(--color-canvas-default);
    border-top: 1px solid var(--color-border-muted);
  }

  table img {
    background-color: transparent;
    max-width: 100%;
    height: auto;
    display: block;
  }
  /* Table-specific styling */
  table {
    border-collapse: collapse;
    margin: 0;
    overflow: hidden;
    table-layout: fixed;
    width: 100%;
    max-width: 100%;

    td,
    th {
      border: 1px solid #dddddd;
      box-sizing: border-box;
      min-width: 1em;
      padding: 8px;
      position: relative;
      vertical-align: top;

      word-wrap: break-word;
      > * {
        margin-bottom: 0;
      }
    }

    td[style],
    th[style] {
      width: auto !important;
      max-width: 100% !important;
    }

    th {
      background-color: var(--gray-1);
      font-weight: bold;
      text-align: left;
    }

    .selectedCell:after {
      background-color: rgb(0, 173, 239, 0.2);
      content: '';
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      pointer-events: none;
      position: absolute;
      z-index: 2;
    }

    .column-resize-handle {
      background-color: transparent;
      bottom: -2px;
      pointer-events: none;
      position: absolute;
      right: -2px;
      top: 0;
      width: 4px;
    }
  }

  /* Containment for a table carrying an inline width or min-width from stored content, mirroring
     `.readable-content table[style]` in the export stylesheet (`pdfLayoutStyles.js`).
     A SIBLING of the `table` block above, deliberately, and it declares no `width`. Nested inside
     it, this compiled to `table table[style]` - which matches only a table INSIDE another table,
     so the guard never reached a top-level table, and its `width: auto !important` overrode the
     `width: 100%` that `BoundedTableView` pins on every table, collapsing every NESTED table to
     its content width. */
  table[style] {
    max-width: 100% !important;
    min-width: 0 !important;
  }

  .tableWrapper {
    margin: 1rem 0;
    overflow-x: auto;
  }

  &.resize-cursor {
    cursor: ew-resize;
    cursor: col-resize;
  }
}

.tiptap-container {
  box-shadow: rgba(0, 0, 0, 0) 0px 0px 0px 0px;
  border: 1px solid rgb(229, 231, 235);
  border-radius: 0.5rem;
  overflow: hidden;
}
.editor-content-container {
  // border: 2px solid #dddddd;
  overflow: auto;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 100%;
  background-color: rgb(245, 245, 245);
}

.editor-content-container.is-print-preview {
  background-color: rgb(232, 232, 232);
  padding: 24px 0;

  .editor__content {
    width: var(--pdf-content-width);
    box-sizing: content-box;
    padding: var(--pdf-preview-page-padding);
    margin: 0 auto;
    background-color: white;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
  }

  :deep(.tableWrapper) {
    overflow-x: visible;
  }

  :deep(table) {
    overflow: visible;
    width: 100% !important;
  }

  :deep(.tiptap-editor) {
    margin: 0;
    padding: 0;
    font-size: inherit;
    background-image: repeating-linear-gradient(
      to bottom,
      transparent 0,
      transparent calc(var(--pdf-content-height) - 1px),
      #f4433680 calc(var(--pdf-content-height) - 1px),
      #f4433680 var(--pdf-content-height)
    );
  }
}
.editor__content {
  background-color: white;
  word-wrap: break-word;

  :deep(.ProseMirror) {
    &:focus-visible {
      outline: none !important;
    }
  }

  overflow-y: auto;
  scroll-behavior: smooth;
  scrollbar-width: thin;
  cursor: text;
  //   max-width: 80vw;
  width: 100%;
  margin: 0px auto;
}

.v-dialog .editor__content {
  height: var(--editor-content-height, 20rem) !important;
}

:deep(.tiptap-editor) {
  outline: none;
  margin: 10px;
}

.control-group {
  display: flex;
  flex-wrap: wrap;
  background-color: rgb(245, 245, 245);
  // gap: 0.5rem;
  border-bottom: 1px solid rgb(229, 231, 235);
}
</style>
